import { Condition } from 'types/segments/segments.types';
import { ISegment } from 'types/segments/segments.types';

/**
 * Get operator label for display
 */
export const getOperatorLabel = (operator: string): string => {
  const operatorLabels: { [key: string]: string } = {
    in_segment: 'is in segment',
    not_in_segment: 'is not in segment',
    equals: 'equals',
    not_equals: 'does not equal',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    greater_than: 'is greater than',
    less_than: 'is less than',
    greater_than_or_equal: 'is greater than or equal to',
    less_than_or_equal: 'is less than or equal to',
    between: 'is between',
    is_timestamp: 'is timestamp',
    is_not_timestamp: 'is not timestamp',
    is_before: 'is before',
    is_after: 'is after',
    is_between: 'is between',
    exists: 'exists',
    not_exists: 'does not exist',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
  };
  return operatorLabels[operator] || operator;
};

/**
 * Format condition value for display
 */
export const formatConditionValue = (value: any): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    if (value.start && value.end) {
      return `${value.start} to ${value.end}`;
    }
    if (value.dateType === 'relative_date' && value.days) {
      return `${value.days} days ${value.timeDirection || ''}`;
    }
    if (value.dateType === 'specific_value' && value.date) {
      return value.date;
    }
    return JSON.stringify(value);
  }
  return String(value);
};

/**
 * Get segment name by ID
 */
export const getSegmentName = (
  segmentId: string,
  segments: ISegment[]
): string => {
  const segment = segments.find((seg) => seg._id === segmentId);
  return segment ? segment.name : segmentId;
};

/**
 * Check if condition is a segment condition
 */
export const isSegmentCondition = (
  condition: Condition,
  segments: ISegment[]
): boolean => {
  return segments.some((segment) => segment._id === condition.field);
};

/**
 * Check if condition is a group
 */
export const isGroupCondition = (condition: Condition): boolean => {
  return condition.condition === 'group';
};

/**
 * Count total conditions including nested groups
 */
export const countConditions = (conditions: Condition[]): number => {
  return conditions.reduce((count, condition) => {
    if (isGroupCondition(condition)) {
      const groupConditions = (condition.value as any)?.conditions || [];
      return count + countConditions(groupConditions);
    }
    return count + 1;
  }, 0);
};

/**
 * Check if trigger node has conditions
 */
export const hasTriggerConditions = (conditions: {
  [key: string]: Condition[];
}): boolean => {
  return (
    Object.keys(conditions).length > 0 &&
    (conditions.and?.length > 0 || conditions.or?.length > 0)
  );
};

const EVENT_FIELD_PREFIX = 'event.';

/**
 * Display name for event condition field (strip "event." prefix)
 */
export const getEventFieldDisplayName = (field: string | undefined): string => {
  if (!field || typeof field !== 'string') return 'Field';
  if (field.startsWith(EVENT_FIELD_PREFIX)) {
    return field.slice(EVENT_FIELD_PREFIX.length).trim() || field;
  }
  return field;
};

/**
 * Symbol/label for event attribute operators in node display
 */
export const getEventOperatorDisplay = (operator: string): string => {
  const map: Record<string, string> = {
    equals: '=',
    not_equals: '≠',
    contains: 'contains',
    starts_with: 'starts with',
    ends_with: 'ends with',
    exists: 'exists',
    not_exists: 'does not exist',
    is_empty: 'empty',
    is_not_empty: 'not empty',
  };
  return map[operator] ?? operator;
};

function formatTriggerConditionsCount(conditions: {
  [key: string]: Condition[];
}): string {
  if (!hasTriggerConditions(conditions)) return '';

  const conditionType = Object.keys(conditions)[0] || 'and';
  const conditionList = conditions[conditionType] || [];
  if (conditionList.length === 0) return '';

  const totalCount = countConditions(conditionList);
  return `${totalCount} condition${totalCount !== 1 ? 's' : ''}`;
}

export const formatEventTriggerConditionsForNode = formatTriggerConditionsCount;

export const formatTriggerConditionsForNode = (
  conditions: { [key: string]: Condition[] },
  _segments: ISegment[]
): string => formatTriggerConditionsCount(conditions);
